/**
 * Extracts and formats J-code/ICD mapping data from a MedicalPaOrder
 * for the purpose of logging or analytics.
 *
 * This helper normalizes the J-code entries inside `auth_on_file.entries`
 * (falling back to `auth_entries`) into a flat array that is log-friendly. Each returned object represents
 * a single J-code along with its associated NCCN match/ICD state.
 *
 * Intended usage:
 *  - Logging J-code data to BigQuery
 *  - Sending structured analytics events
 *  - Auditing J-code → NCCN/ICD mapping
 *
 * @param {MedicalPaOrder} singleMedicalPaOrder - The current PA order from which
 * J-code and NCCN/ICD information should be extracted.
 *
 * @returns {{ jCode: string; icd_state: string }[]}
 * A flattened list of objects where each object contains:
 *  - `jCode`: The J-code string used for logging
 *  - `icd_state`: The NCCN match or ICD state associated with that J-code
 *
 * Returns an empty array if the PA order contains no auth entries or J-codes.
 */
import {
  AuthOnFile,
  AuthRangeResponse,
  JCodeDetails,
  MedicalPaOrder,
} from "../../../data-model/medicalPaOrdersModel";
export function formatJcodeIcd(singleMedicalPaOrder: MedicalPaOrder) {
  if (!singleMedicalPaOrder) return [];

  const entries =
    singleMedicalPaOrder.auth_on_file?.entries ??
    singleMedicalPaOrder.auth_on_file?.auth_entries ??
    [];

  return entries.flatMap((entry: AuthRangeResponse) =>
    (entry.j_codes ?? []).map((j: JCodeDetails) => ({
      jCode: j.j_code ?? "",
      icd_state: j.nccn_match ?? "",
    })),
  );
}

/**
 * Extracts and formats J-code/ICD mapping data directly from an AuthOnFile
 * object (Postgres-backed) instead of the legacy MedicalPaOrder wrapper.
 *
 * @param authOnFile - The auth-on-file details from the data-accumulator context
 * @returns Flattened list of `{ jCode, icd_state }` pairs; empty array when
 *          the input is nullish or contains no auth entries.
 */
export function formatJcodeIcdFromAuthOnFile(
  authOnFile: AuthOnFile | null | undefined,
): { jCode: string; icd_state: string }[] {
  if (!authOnFile) return [];

  const entries = authOnFile.entries ?? authOnFile.auth_entries ?? [];

  return entries.flatMap((entry: AuthRangeResponse) =>
    (entry.j_codes ?? []).map((j: JCodeDetails) => ({
      jCode: j.j_code ?? "",
      icd_state: j.nccn_match ?? "",
    })),
  );
}
